import { z } from 'zod';
import { DEFAULT_KIT_ACCENT, KIT_ACCENTS } from '@/lib/media-kit/accent-palette';
import { BIO_ACCENTS, BIO_ACCENT_DESCRIPTION } from './accent-palette';

export const slugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3, 'Use at least 3 characters for your page address.')
  .max(32, 'Use 32 characters or fewer for your page address.')
  .regex(
    /^[a-z0-9]+(?:[._-][a-z0-9]+)*$/,
    'Use letters, numbers and single dots, underscores or hyphens.',
  )
  .refine(
    (value) =>
      !['admin', 'support', 'owlscran', 'help', 'www', 'api', 'sitemap.xml'].includes(value),
    'Choose another address.',
  );

export function safeLink(value: string): boolean {
  try {
    const url = new URL(value);
    return (
      url.protocol === 'https:' &&
      !url.username &&
      !url.password &&
      url.hostname.includes('.') &&
      !url.hostname.endsWith('.local') &&
      !url.hostname.endsWith('.localhost') &&
      !/^[\d.]+$/.test(url.hostname) &&
      !url.hostname.includes(':')
    );
  } catch {
    return false;
  }
}

// Email buttons accept one recipient, without headers, encoded control characters
// or other URI features. Keep network/image validation HTTPS-only via safeLink.
export function safeBioDestination(value: string): boolean {
  return (
    safeLink(value) ||
    /^mailto:[a-zA-Z0-9.!#$&'*+/=^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(
      value,
    )
  );
}

export function bioDestinationLabel(value: string): string {
  if (value.startsWith('mailto:') && safeBioDestination(value)) return value.slice(7);
  try {
    return new URL(value).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

// Local Supabase serves uploaded avatars over HTTP. Keep this development-only
// exception scoped to our configured avatars bucket, never arbitrary link targets.
function safeAvatar(value: string): boolean {
  if (value === '' || safeLink(value)) return true;
  if (process.env.NODE_ENV === 'production') return false;
  try {
    const base = new URL(process.env.NEXT_PUBLIC_SUPABASE_URL ?? '');
    const image = new URL(value);
    return (
      base.protocol === 'http:' &&
      ['localhost', '127.0.0.1'].includes(base.hostname) &&
      image.origin === base.origin &&
      !image.username &&
      !image.password &&
      image.pathname.startsWith('/storage/v1/object/public/avatars/')
    );
  } catch {
    return false;
  }
}

const linkAddressError = 'Use a full https:// address or mailto:email@example.com.';
const imageAddressError = 'Use an HTTPS image.';
export const bioItemSchema = z.object({
  id: z.string().uuid(),
  title: z.string().trim().min(1, 'Add a title.').max(80),
  url: z.string().trim().max(2048).refine(safeBioDestination, linkAddressError),
  image: z.string().max(2048).refine(safeAvatar, imageAddressError).optional(),
  icon: z.string().max(2048).refine(safeAvatar, imageAddressError).optional(),
  description: z.string().trim().max(240).optional(),
});
export const socialPlatforms = [
  'instagram',
  'tiktok',
  'youtube',
  'facebook',
  'strava',
  'website',
] as const;
export const bioSocialSchema = z.object({
  platform: z.enum(socialPlatforms),
  url: z.string().trim().max(2048).refine(safeLink, linkAddressError),
});
export type BioItem = z.infer<typeof bioItemSchema>;

export const bioBlockSchema = z
  .object({
    id: z.string().uuid(),
    type: z.enum(['link', 'heading', 'text', 'collection', 'message']),
    title: z.string().trim().min(1, 'Add a title.').max(80),
    description: z.string().trim().max(240),
    url: z.string().trim().max(2048),
    visible: z.boolean(),
    featured: z.boolean(),
    image: z.string().max(2048).refine(safeAvatar, imageAddressError).optional(),
    icon: z.string().max(2048).refine(safeAvatar, imageAddressError).optional(),
    display: z.enum(['compact', 'card', 'expandable']).optional(),
    items: z.array(bioItemSchema).max(40, 'Add up to 40 items per collection.').optional(),
    buttonText: z.string().trim().min(1).max(40).optional(),
  })
  .superRefine((block, ctx) => {
    if (block.type === 'collection' && !block.items?.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['items'],
        message: 'Add at least one item to your collection.',
      });
    }
    if (block.items && new Set(block.items.map((item) => item.id)).size !== block.items.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['items'],
        message: 'Collection items must have distinct IDs.',
      });
    }
    if (block.type === 'link' && !safeBioDestination(block.url)) {
      ctx.addIssue({
        code: 'custom',
        path: ['url'],
        message: linkAddressError,
      });
    }
  });

export const bioDocumentSchema = z
  .object({
    name: z.string().trim().min(1, 'Add your display name.').max(80),
    bio: z.string().trim().max(240),
    avatar: z.string().max(2048).refine(safeAvatar, imageAddressError),
    avatarRemoved: z.boolean().optional(),
    avatarShape: z.enum(['circle', 'rounded']).optional(),
    cover: z.string().max(2048).refine(safeAvatar, imageAddressError).optional(),
    coverPosition: z.number().min(0).max(100).optional(),
    showFollowerCount: z.boolean().optional(),
    accent: z
      .enum(BIO_ACCENTS.map((accent) => accent.hex) as [string, ...string[]])
      .describe(BIO_ACCENT_DESCRIPTION),
    appearance: z.enum(['light', 'dark']),
    showBranding: z.boolean(),
    socials: z.array(bioSocialSchema).max(6).optional(),
    blocks: z.array(bioBlockSchema).max(50, 'You can add up to 50 sections.'),
  })
  .superRefine((doc, ctx) => {
    if (
      new Set(doc.socials?.map((social) => social.platform)).size !== (doc.socials?.length ?? 0)
    ) {
      ctx.addIssue({ code: 'custom', path: ['socials'], message: 'Add each social profile once.' });
    }
    if (new Set(doc.blocks.map((b) => b.id)).size !== doc.blocks.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['blocks'],
        message: 'Sections must have distinct IDs.',
      });
    }
    if (doc.blocks.filter((b) => b.featured && b.type === 'link').length > 1) {
      ctx.addIssue({ code: 'custom', path: ['blocks'], message: 'Choose one featured link.' });
    }
  });

export type BioDocument = z.infer<typeof bioDocumentSchema>;

/**
 * Something a visitor can act on: a visible link, collection or message form. Social
 * icons alone don't count: they render only while the account stays connected, so a
 * socials-only page would go blank after a disconnect.
 */
export function hasPublishableContent(document: BioDocument): boolean {
  return document.blocks.some(
    (block) =>
      block.visible &&
      (block.type === 'link' || block.type === 'collection' || block.type === 'message'),
  );
}

export const UNPUBLISHABLE_BIO_MESSAGE =
  'Add a link, collection or contact form before publishing.';

export type BioBlock = BioDocument['blocks'][number];
export type BioPage = {
  address_change_used: boolean;
  id: string;
  slug: string;
  draft: BioDocument;
  published: BioDocument | null;
  revision: number;
};
export type BioSaveResult =
  | { page: BioPage; error?: never; code?: never }
  | { error: string; page?: never; code?: 'address_taken' };
export type BioSave = (input: {
  slug: string;
  document: BioDocument;
  revision: number;
  intent: 'save' | 'publish' | 'unpublish';
}) => Promise<BioSaveResult>;

export function emptyDocument(profile?: {
  full_name?: string | null;
  bio?: string | null;
  avatar_url?: string | null;
  media_kit_accent?: string | null;
}): BioDocument {
  return {
    name: profile?.full_name?.slice(0, 80) ?? '',
    bio: profile?.bio?.slice(0, 240) ?? '',
    avatar: profile?.avatar_url && safeAvatar(profile.avatar_url) ? profile.avatar_url : '',
    accent:
      KIT_ACCENTS.find((a) => a.hex === profile?.media_kit_accent)?.hex ?? DEFAULT_KIT_ACCENT.hex,
    appearance: 'dark',
    showBranding: true,
    blocks: [],
  };
}

// Older drafts may have been saved before the media-kit photo was inherited.
// Keep an explicit removal distinct from that missing default.
export function withProfileAvatar(document: BioDocument, profileAvatar = ''): BioDocument {
  if (document.avatar || document.avatarRemoved || !profileAvatar || !safeAvatar(profileAvatar)) {
    return document;
  }
  return { ...document, avatar: profileAvatar };
}

export function trafficSource(referrer: string): string {
  try {
    const host = new URL(referrer).hostname;
    for (const [domain, source] of [
      ['instagram.com', 'Instagram'],
      ['tiktok.com', 'TikTok'],
      ['youtube.com', 'YouTube'],
      ['youtu.be', 'YouTube'],
      ['facebook.com', 'Facebook'],
      ['google.com', 'Google'],
      ['google.co.uk', 'Google'],
      ['reddit.com', 'Reddit'],
      ['redd.it', 'Reddit'],
    ] as const) {
      if (host === domain || host.endsWith(`.${domain}`)) return source;
    }
    return 'Other';
  } catch {
    return 'Direct or unknown';
  }
}
